import { Controller } from "@hotwired/stimulus"

/*
Specific to DropdownFilterComponent
The idea is to decorate a normal <select> (which becomes hidden) with a button that triggers a dropdown 
showing all the options in a text-searchable list of checkboxes.
NOTE:
- The dropdown is populated from the initial state of the <select> - there's no attempt yet to reconcile subsequent
changes to the <select> or anything complicated like that. 
*/
export default class extends Controller {
  static targets = [
    "trigger", 
    "dropdown", 
    "checkboxItemTemplate", 
    "radioItemTemplate", 
    "groupTemplate", 
    "selectedCount", 
    "selectAll", 
    "selectAllContainer", 
    "search", 
    "itemList", 
    "showAll", 
    "showSelected", 
    "clearSelection", 
    "clearSelectionContainer",
    "buttonLabel"
  ] 

  static values = {
    // when true, "change" events will be triggered immediately as checkboxes are checked/unchecked
    // when false, a "change" event will only be triggered once the dropdown hides
    changeOnClick: { type: Boolean, default: true },
    // enable a checkbox in the dropdown that auto-selects all the options. only applicable for a <select multiple>
    enableSelectAll: { type: Boolean, default: true },
    // when true, the default behavior of Turbo is maintained - any user selections will be preserved in the
    //   Turbo cache, so they will still be present after navigating away and then using the browser "back" button
    // when false, selections will be reverted back to the starting HTML when going back
    // Example: this should probably be "false" if you want the current URL to dictate the state of the page
    // Research: https://github.com/hotwired/turbo/issues/894
    turboCacheChanges: { type: Boolean, default: true },
  } 

  connect() {
    this.triggerChangeEvent = this.debounce(this.triggerChangeEvent.bind(this), 300);
    this.pendingChangeEvent = false;
    this.uniqueId = new Date().getTime();

    this.selectElement = this.element.querySelector("select");
    if (!this.selectElement) { throw new Error("A <select> element is required") }

    // hide the "select all" feature when applicable
    if (!this.enableSelectAllValue) {
      this.selectAllContainerTarget.classList.add("hidden");
    }

    // hide various elements if only a single selection is possible
    if (!this.selectElement.multiple) {
      this.selectedCountTarget.classList.add("hidden");
      this.selectAllContainerTarget.classList.add("hidden");
      this.clearSelectionContainerTarget.classList.add("hidden");
    }

    // decide how tall the list of options should be
    if (this.enableSelectAllValue || !this.selectElement.multiple) {
      this.itemListTarget.classList.add("max-h-48");
    } else {
      this.itemListTarget.classList.add("max-h-80");
    }

    this.dropdown = new Dropdown(this.dropdownTarget, this.triggerTarget, {
      // triggerType: "none",
      placement: "bottom-start",
      onShow: () => {
        this.triggerTarget.classList.add("bg-sand");
      },
      onHide: () => {
        this.triggerTarget.classList.remove("bg-sand");

        if (this.pendingChangeEvent) {
          this.pendingChangeEvent = false;
          this.selectElement.dispatchEvent(this.buildChangeEvent());
        }
      }
    });

    if (!this.turboCacheChangesValue) {
      this.resetFromHTML();
    }

    this.itemListTarget.addEventListener("change", (ev) => {
      // update underlying <select> element
      const escapedValue = CSS.escape(ev.target.value);
      this.selectElement.querySelector(`option[value="${escapedValue}"]`).selected = ev.target.checked;

      // disable 'select all' any time an item is unchecked
      if (!ev.target.checked) {
        this.selectAllTarget.checked = false;
      }

      this.updateTriggerButtonLabels();
      this.triggerChangeEvent();
    });

    this.showAllTarget.addEventListener("click", () => {
      this.setShowingSelectedOnly(false);
      this.updateItemList();
    });

    this.showSelectedTarget.addEventListener("click", () => {
      this.setShowingSelectedOnly(true);
      this.updateItemList();
    });

    this.selectAllTarget.addEventListener("change", (ev) => {
      if (ev.target.checked) {
        this.updateItemList();
      } else {
        this.clearSelected();
      }
    });    

    this.clearSelectionTarget.addEventListener("click", () => {
      if (this.clearSelectionTarget.classList.contains("cursor-pointer")) {
        this.clearSelected();
      }
    });

    this.searchTarget.addEventListener("input", () => {
      this.updateItemList();
    });    

    this.buildItemList();
    this.updateItemList(false);
    this.hideBlankLineOption(); // Options for single select
  }

  disconnect() {
    this.itemListTarget.innerHTML = "";
  }

  highlightGroupByName(name) {
    if (typeof(name) === "string") {
      name = name.toLowerCase();
    } else {
      name = null;
    }
    const groups = this.itemListTarget.querySelectorAll("[name=group]");

    groups.forEach((group) => {
      const label = group.querySelector("[name=label]");
      const items = group.querySelector("[name=items]");
      const anySelectedItems = items.querySelectorAll('input:checked').length > 0;

      // expand the group if no group was specified, the group's name matches, or the group has
      // any selected items. otherwise collapse it.
      const expand = (name === null || name === label.textContent.toLowerCase() || anySelectedItems);
      this.toggleGroup(group, expand);
    });
  }

  toggleGroup(group, expanded) {
    const header = group.querySelector("[name=header]");
    const iconContainer = group.querySelector("[name=icon-container");
    const items = group.querySelector("[name=items]");

    if (expanded === undefined) {
      expanded = iconContainer.classList.contains("rotate-180");
    }
    
    iconContainer.classList.toggle("rotate-180", !expanded);
    items.classList.toggle("hidden", !expanded);
    header.classList.toggle("text-charcoal-50", !expanded)    
  }

  buildItemList() {
    const optgroups = this.selectElement.querySelectorAll("optgroup");
    if (optgroups.length > 0) {

      optgroups.forEach((optgroup, groupIndex) => {
        const group = this.groupTemplateTarget.content.firstElementChild.cloneNode(true);
        const header = group.querySelector("[name=header]");
        const label = group.querySelector("[name=label]");
        const items = group.querySelector("[name=items]");

        label.textContent = optgroup.label;
        items.classList.toggle("border-b", groupIndex != optgroups.length - 1);

        header.addEventListener("click", () => {
          this.toggleGroup(group)
        });

        const options = optgroup.querySelectorAll("option");
        options.forEach((option) => {
          this.buildItem(option.label, option.value, items);
        })

        this.itemListTarget.appendChild(group);
      })

    } else {
      for (const option of this.selectElement) {
        this.buildItem(option.label, option.value, this.itemListTarget);
      } 
    }
  }

  buildItem(label, value, destination) {
    const template = this.selectElement.multiple ? this.checkboxItemTemplateTarget : this.radioItemTemplateTarget;

    if (!this.itemTemplateLabel) {
      this.itemTemplateLabel = template.content.querySelector("label");
    }
    if (!this.itemTemplateCheckbox) {
      this.itemTemplateCheckbox = template.content.querySelector("input");
    }
    var id = `${this.uniqueId}-${value}`;
    this.itemTemplateLabel.textContent = label;
    this.itemTemplateLabel.setAttribute("for", id);
    this.itemTemplateCheckbox.value = value;
    this.itemTemplateCheckbox.id = id;
    
    const item = template.content.firstElementChild.cloneNode(true);
    destination.appendChild(item)
  }

  updateItemList(allowTriggerEvents = true) {
    const options = this.selectElement.querySelectorAll("option");
    const selectAll = this.selectAllTarget.checked;
    const showSelectedOnly = this.isShowingSelectedOnly();
    let triggerEvent = false;

    for (const option of options) {
      // find the checkbox corresponding to this <select> option
      const escapedValue = CSS.escape(option.value);
      const itemCheckbox = this.itemListTarget.querySelector(`input[value="${escapedValue}"]`);

      // hide item if filtered out by search
      const searchTerm = this.searchTarget.value.toLowerCase();
      const label = itemCheckbox.parentElement.querySelector("label").textContent;
      let itemVisible = (!searchTerm || label.toLowerCase().includes(searchTerm));  

      if (itemVisible && selectAll) {
        // if select all is enabled, any visible checkbox gets checked
        if (!itemCheckbox.checked) {
          triggerEvent = true;
        }
        itemCheckbox.checked = true
        option.selected = true;
      } else {
        // just update the checkbox to reflect the underyling <select> option
        if (itemCheckbox.checked != option.selected) {
          triggerEvent = true;
          itemCheckbox.checked = option.selected
        }        
      }

      if (showSelectedOnly && !option.selected)
        itemVisible = false;

      itemCheckbox.parentElement.classList.toggle("hidden", !itemVisible);      
    }

    this.updateTriggerButtonLabels();
    this.hideBlankLineOption();
    if (triggerEvent && allowTriggerEvents) {
      this.triggerChangeEvent();
    }
  }

  /* The current drop down - single select will default to the first option. We are allowing
   the include_blank property which causing a blank line default. For user experience, we are
   manually hiding the line. */
  hideBlankLineOption() {
    if (this.selectElement.multiple) return;

    let isBlankLabel = this.selectElement.options[0].text.length === 0;
    if (isBlankLabel) {
      let blankLine = this.itemListTarget.children[0]; // If there's a blank line, we are going to hide the line item.
      blankLine.classList.add("hidden");
    }
  }

  /* Parent method which updates the button label depending on if it's a single or multiple
  select dropdown.
   */
  updateTriggerButtonLabels() {
    if (this.selectElement.multiple) {
      this.updateSelectedCount()
    } else {
      this.updateLabelForSingleSelected()
    }
  }

  /* The current drop down uses a button as the trigger source. In order to display
  the selected value for the single select use case, we need to update the button's text. */
  updateLabelForSingleSelected() {
    let optionIndex = this.selectElement.selectedIndex;
    let selectedOption = this.selectElement.options[optionIndex];
    if (selectedOption.text) { // avoid over-writing label when include_blank is set
      this.buttonLabelTarget.textContent = selectedOption.text;
    }
  }

  updateSelectedCount() {
    const options = Array.from(this.selectElement.querySelectorAll("option"));
    const selectedCount = options.filter((option) => option.selected).length;

    // update the selection count on the dropdown button
    this.selectedCountTarget.textContent = selectedCount > 0 ? `(${selectedCount})` : "";

    // deactivate the "clear selection" link at bottom of dropdown if there is nothing to clear
    this.clearSelectionTarget.classList.toggle("text-forest", selectedCount > 0);
    this.clearSelectionTarget.classList.toggle("cursor-pointer", selectedCount > 0);    
  }

  clearSelected() {
    this.selectElement.selectedIndex = -1;
    this.selectAllTarget.checked = false;
    this.updateItemList();
  }

  setShowingSelectedOnly(enabled) {
    if (enabled) {
      this.selectAllTarget.checked = false;
    }

    this.showSelectedTarget.classList.toggle("font-bold", enabled);
    this.showSelectedTarget.classList.toggle("cursor-pointer", !enabled);
    this.showAllTarget.classList.toggle("font-bold", !enabled);
    this.showAllTarget.classList.toggle("cursor-pointer", enabled);
  }

  isShowingSelectedOnly() {
    return this.showSelectedTarget.classList.contains("font-bold");
  }

  triggerChangeEvent() {
    if (this.changeOnClickValue) {
      this.selectElement.dispatchEvent(this.buildChangeEvent());
    } else {
      this.pendingChangeEvent = true;
    }
  }

  buildChangeEvent() {
    return new Event("change");
  }

  debounce(fn, delay) {
    let timeoutId = null
  
    return (...args) => {
      const callback = () => fn.apply(this, args)
      clearTimeout(timeoutId)
      timeoutId = setTimeout(callback, delay)
    }
  }

  resetFromHTML() {
    this.dropdown.hide();
    this.searchTarget.value = this.searchTarget.defaultValue;
    this.selectAllTarget.checked = this.selectAllTarget.defaultChecked;
    for (const option of this.selectElement) {
      option.selected = option.defaultSelected;
    }    
  }
}